import { useState, useEffect, useMemo, useRef, useCallback } from "react";

import { css } from "@compiled/react";
import { useIntl, FormattedMessage } from "react-intl";
import { IntlProvider } from "react-intl-next";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import { LoadingButton } from "@atlaskit/button";
import InformationCircleIcon from "@atlaskit/icon/core/information-circle";
import Link from "@atlaskit/link";
import { Inline, Text } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";
import { USER_SEGMENTATION_TEAM_TYPE_SOFTWARE } from "@atlassian/onboarding-survey";
import { MadlibSetupPrompt } from "@atlassian/product-setup";

import { useAnalyticsClient } from "@/analytics/wac-analytics/analytics-client";
import { useSearchParameters } from "@/components/signup/hooks/use-search-parameters";
import {
    ufoEventMadlibProjectCreation,
    ufoEventSkippedMadlibProjectCreation
} from "@/components/signup/utils/ufo-events";
import { UfoCustomTimingKey } from "@/components/signup/utils/ufo.types";

import { useCloudStatus } from "../../../../../hooks/use-cloud-status";
import { StepContext, StepProps } from "../../../types";
import { Header } from "../../components/header";

import { stepMessages } from "./messages";
import { useSuggestionsForTeamType } from "./suggestions";

export type MadlibStepContext = {
    data?: {
        task: string;
        goal: string;
        dueDate: number;
    };
} & StepContext;

export const Madlib = <T extends StepContext>({
    context,
    setContext,
    nextStep,
    onSubmit,
    onSkip
}: StepProps<T>) => {
    const intl = useIntl();
    const { createCloudStatus } = useCloudStatus();
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const [formSubmitted, setFormSubmitted] = useState(false);
    const [formSkipped, setFormSkipped] = useState(false);
    const { signupId } = useSearchParameters();
    const onSubmitTriggered = useRef(false);
    const onSkipTriggered = useRef(false);
    const analyticsClient = useAnalyticsClient();

    const [header] = useState(intl.formatMessage(stepMessages.header));
    const [subheader] = useState(intl.formatMessage(stepMessages.subheader));

    const { teamType = USER_SEGMENTATION_TEAM_TYPE_SOFTWARE, dueDate } =
        context as MadlibStepContext;

    const suggestions = useSuggestionsForTeamType(teamType);

    const { defaultValue } = useMemo(() => {
        const [suggestion] = suggestions;
        if (!suggestion) {
            return {
                defaultValue: {
                    task: "",
                    goal: "",
                    dueDate
                }
            };
        }

        const defaultValue = suggestion.data;

        return { defaultValue };
    }, [suggestions, dueDate]);

    const onFormSubmit = async (data: object | undefined) => {
        // set request in in progress to show loading state
        setFormSubmitted(true);

        setContext({
            ...context,
            data
        });
        createAnalyticsEvent({
            action: "clicked",
            actionSubject: "button",
            actionSubjectId: "madlibStepSubmitButton",
            eventType: "ui",
            attributes: {
                teamType: context.teamType || "unknown"
            }
        }).fire();
    };

    const onFormSkip = () => {
        setFormSkipped(true);
        createAnalyticsEvent({
            action: "clicked",
            actionSubject: "button",
            actionSubjectId: "madlibStepSkipButton",
            eventType: "ui",
            attributes: {
                teamType: context.teamType || "unknown"
            }
        }).fire();
    };

    useEffect(() => {
        if (!analyticsClient) {
            return;
        }
        createAnalyticsEvent({
            action: "viewed",
            actionSubject: "screen",
            actionSubjectId: "madlibStep",
            eventType: "screen",
            name: "madlibStep",
            attributes: {
                teamType: context.teamType || "unknown"
            }
        }).fire();
    }, [createAnalyticsEvent, analyticsClient, context.teamType]);

    const createDefaultProject = useCallback(async () => {
        if (onSkip) {
            onSkipTriggered.current = true;
            ufoEventSkippedMadlibProjectCreation.start();
            ufoEventSkippedMadlibProjectCreation.setCustomData({ signupId });
            ufoEventSkippedMadlibProjectCreation.startSubstep(
                UfoCustomTimingKey.JIRA_ONBOARDING_DEFAULT_PROJECT_CREATE
            );
            return onSkip({ context, setContext })
                .catch((error) => {
                    console.error(
                        "Error during Madlib prompt submission:",
                        error
                    );
                    ufoEventSkippedMadlibProjectCreation.failure({ error });
                })
                .finally(() => {
                    ufoEventSkippedMadlibProjectCreation.success();
                });
        }
    }, [context, onSkip, setContext, signupId]);

    const createMadlibProject = useCallback(async () => {
        if (onSubmit) {
            ufoEventMadlibProjectCreation.start();
            ufoEventMadlibProjectCreation.setCustomData({ signupId });
            ufoEventMadlibProjectCreation.startSubstep(
                UfoCustomTimingKey.JIRA_ONBOARDING_MADLIB_PROJECT_CREATE
            );
            return onSubmit({ context, setContext })
                .catch((error) => {
                    console.error(
                        "Error during Madlib prompt submission:",
                        error
                    );
                    ufoEventMadlibProjectCreation.failure({ error });
                })
                .finally(() => {
                    ufoEventMadlibProjectCreation.success();
                });
        }
    }, [context, onSubmit, setContext, signupId]);

    useEffect(() => {
        if (!formSubmitted || createCloudStatus !== "success") {
            return;
        }

        if (!onSubmitTriggered.current) {
            onSubmitTriggered.current = true;

            // trigger project creation and store promise
            const projectCreatePromise = createMadlibProject();

            // add project creation to promises to resolve
            setContext((prevContext) => ({
                ...prevContext,
                promisesToResolve: prevContext.promisesToResolve.concat([
                    projectCreatePromise
                ])
            }));

            // move to next step
            nextStep();
        }
    }, [
        nextStep,
        createCloudStatus,
        createMadlibProject,
        formSubmitted,
        setContext
    ]);

    useEffect(() => {
        if (!formSkipped || createCloudStatus !== "success") {
            return;
        }

        if (!onSkipTriggered.current) {
            onSkipTriggered.current = true;

            // trigger default project creation and store promise
            const defaultProjectPromise = createDefaultProject();

            // add default project creation to promises to resolve
            setContext((prevContext) => ({
                ...prevContext,
                promisesToResolve: prevContext.promisesToResolve.concat([
                    defaultProjectPromise
                ])
            }));

            // move to next step
            nextStep();
        }
    }, [
        context,
        createCloudStatus,
        createDefaultProject,
        formSkipped,
        nextStep,
        onSkipTriggered,
        setContext
    ]);

    return (
        <IntlProvider locale={intl.locale}>
            <div css={container}>
                <Header header={header} subheader={subheader} />
                <MadlibSetupPrompt
                    onSubmit={onFormSubmit}
                    defaultValue={defaultValue}
                    suggestions={suggestions}
                    ActionFooterComponent={
                        <ActionFooterComponent
                            onSkip={onFormSkip}
                            formSkipped={formSkipped}
                            formSubmitted={formSubmitted}
                        />
                    }
                />
            </div>
        </IntlProvider>
    );
};

export const ActionFooterComponent = ({
    onSkip,
    formSkipped,
    formSubmitted
}: {
    onSkip: () => void;
    formSkipped: boolean;
    formSubmitted: boolean;
}) => {
    const [submitDisabledOnLoad, setSubmitDisabledOnLoad] = useState(true);

    useEffect(() => {
        // Disable submit button for 500 milliseconds to prevent rapid clicks
        if (submitDisabledOnLoad) {
            const timer = setTimeout(() => setSubmitDisabledOnLoad(false), 500);
            return () => clearTimeout(timer);
        }
    }, [submitDisabledOnLoad]);

    const shouldDisableButtons = useMemo(
        () => formSubmitted || formSkipped,
        [formSubmitted, formSkipped]
    );

    return (
        <div css={actionFooterContainer}>
            <AiDisclaimer />
            <div css={actionFooterButtonContainer}>
                <LoadingButton
                    appearance="subtle"
                    testId="skip-button"
                    onClick={() => {
                        onSkip();
                    }}
                    isDisabled={shouldDisableButtons}
                    isLoading={formSkipped}
                >
                    <FormattedMessage {...stepMessages.skipButton} />
                </LoadingButton>
                <LoadingButton
                    appearance="primary"
                    testId="submit-button"
                    type="submit"
                    isDisabled={submitDisabledOnLoad || shouldDisableButtons}
                    isLoading={formSubmitted}
                >
                    <FormattedMessage {...stepMessages.submitButton} />
                </LoadingButton>
            </div>
        </div>
    );
};

export const AiDisclaimer = () => {
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const { formatMessage } = useIntl();

    const handleClick = () => {
        createAnalyticsEvent({
            action: "clicked",
            actionSubject: "link",
            attributes: {
                componentName: "madlibAiDisclaimerLink",
                location: "madlibStepActionFooter"
            }
        }).fire();
    };

    return (
        <Inline alignBlock="center" space="space.050">
            <InformationCircleIcon
                color={token("color.text.subtlest")}
                label={formatMessage(stepMessages.aiDisclaimerLabel)}
                size="small"
            />
            <Text as="span" color="color.text.subtlest">
                <Link
                    data-testid="ai-disclaimer-link"
                    appearance="subtle"
                    href="https://www.atlassian.com/trust/atlassian-intelligence"
                    onClick={handleClick}
                    target="_blank"
                    newWindowLabel={formatMessage(
                        stepMessages.aiDisclaimerLinkNewWindowLabel
                    )}
                >
                    <FormattedMessage {...stepMessages.aiDisclaimer} />
                </Link>
            </Text>
        </Inline>
    );
};

const container = css({
    height: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between"
});

const actionFooterContainer = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    marginBlockStart: token("space.200", "16px")
});

const actionFooterButtonContainer = css({
    display: "flex",
    gap: token("space.100", "8px")
});
